import { AxiosResponse } from "axios";
import apiService from "../api/apiService";
import {
	MasterCategory,
	Product,
	ProductFilter,
	ProductsResponse,
	ProductType,
} from "../types/productTypes";
import { extractValues } from "../utils/helper";

// API response wrapper type
export interface ApiResponse<T> {
	status: number;
	message: string | null;
	errors: any | null;
	data: T;
	timeStamp: number;
}

export interface Platform {
	id: string;
	name: string;
}

export const ProductController = {
	getMasterCategoriesByBrand: (
		brand: string
	): Promise<
		AxiosResponse<ApiResponse<{ masterCategories: MasterCategory[] }>>
	> => apiService.get(`/products/master-categories/${brand}`),

	getMasterCategories: (): Promise<
		AxiosResponse<ApiResponse<{ masterCategories: MasterCategory[] }>>
	> => apiService.get(`/products/master-categories`),

	getOtherPlatform: (): Promise<
		AxiosResponse<ApiResponse<{ data: Platform[] }>>
	> => apiService.get(`/merchant/other-platforms`),

	getProductTypes: (
		categoryId: string
	): Promise<AxiosResponse<ApiResponse<{ productTypes: ProductType[] }>>> =>
		apiService.get(`/products/product-types/${categoryId}`),

	getProductTypeMetadata: (
		categoryId: string
	): Promise<AxiosResponse<ApiResponse<ProductType>>> =>
		apiService.get(`/products/product-types/${categoryId}`),

	createProduct: (
		data: FormData
	): Promise<AxiosResponse<ApiResponse<Product>>> =>
		apiService.post(`/products`, data),

	deleteProduct: (
		productId: string
	): Promise<AxiosResponse<ApiResponse<any>>> =>
		apiService.delete(`/products/${productId}`),

	deleteVariantMedia: (
		variantId: string,
		mediaKey: string
	): Promise<AxiosResponse<ApiResponse<any>>> =>
		apiService.delete(
			`/products/${variantId}/variant-media?mediaKey=${mediaKey}`
		),

	deleteSizeChart: (
		productId: string
	): Promise<AxiosResponse<ApiResponse<any>>> =>
		apiService.delete(`/products/${productId}/size-chart-media`),

	updateProduct: (
		productId: string,
		data: Partial<Product>
	): Promise<AxiosResponse<ApiResponse<Product>>> => {
		const formData = new FormData();

		// Helper to append if value is not undefined/null
		const appendIfExists = (key: string, value: any) => {
			if (value !== undefined && value !== null) {
				formData.append(key, value);
			}
		};

		appendIfExists("skuCode", data.skuCode);
		appendIfExists("brand", data.brand);
		appendIfExists("productName", data.productName);
		appendIfExists("manufacturer", data.manufacturer);
		appendIfExists("articleNumber", data.articleNumber);
		appendIfExists("description", data.description);
		appendIfExists("categoryId", data.categoryId);
		appendIfExists("productTypeId", data.productTypeId);
		
		if (data.isReturnable !== undefined) {
			formData.append("isReturnable", String(data.isReturnable));
		}
		if (data.isDraft !== undefined) {
			formData.append("isDraft", String(data.isDraft));
		}

		// Metadata (as JSON string)
		if (data.metadata) {
			formData.append("metadata", JSON.stringify(extractValues(data.metadata)));
		}

		// Shipping essentials
		if (data.shippingEssentials) {
			const se = data.shippingEssentials;
			if (se.length !== undefined)
				formData.append("shippingEssentials.length", se.length.toString());
			if (se.breadth !== undefined)
				formData.append("shippingEssentials.breadth", se.breadth.toString());
			if (se.height !== undefined)
				formData.append("shippingEssentials.height", se.height.toString());
			if (se.weight !== undefined)
				formData.append("shippingEssentials.weight", se.weight.toString());
		}

		// Size chart media
		if (data.sizeChartMedia && data.sizeChartMedia instanceof File) {
			formData.append(
				"sizeChartMedia",
				data.sizeChartMedia,
				data.sizeChartMedia.name
			);
		}
		
		// Variants
		if (data.variants && Array.isArray(data.variants)) {
			data.variants.forEach((variant, variantIndex) => {
				if (!variant) return;
				appendIfExists(`variants[${variantIndex}].id`, variant.id);
				appendIfExists(
					`variants[${variantIndex}].externalProductId`,
					variant.externalProductId
				);
				appendIfExists(
					`variants[${variantIndex}].externalProductIdType`,
					variant.externalProductIdType
				);
				appendIfExists(
					`variants[${variantIndex}].relationshipType`,
					variant.relationshipType || "variant"
				);
				appendIfExists(`variants[${variantIndex}].skuCode`, variant.skuCode);
				appendIfExists(`variants[${variantIndex}].name`, variant.name);

				// Variant attributes
				if (
					variant.variantAttributes &&
					Array.isArray(variant.variantAttributes)
				) {
					variant.variantAttributes.forEach((attr, attrIndex) => {
						if (!attr) return;
						appendIfExists(
							`variants[${variantIndex}].variantAttributes[${attrIndex}].attribute`,
							attr.attribute
						);
						appendIfExists(
							`variants[${variantIndex}].variantAttributes[${attrIndex}].value`,
							attr.value
						);
					});
				}

				// Pricing
				if (variant.pricing && variant.pricing.sellingPrice !== undefined) {
					formData.append(
						`variants[${variantIndex}].price`,
						variant.pricing.sellingPrice.toString()
					);
				}

				// Inventory
				if (variant.inventory && variant.inventory.quantity !== undefined) {
					formData.append(
						`variants[${variantIndex}].inventoryQuantity`,
						variant.inventory.quantity.toString()
					);
				}

				// Media (files or strings)
				if (variant.media && Array.isArray(variant.media)) {
					variant.media.forEach((mediaItem) => {
						if (mediaItem instanceof File) {
							formData.append(
								`variants[${variantIndex}].media`,
								mediaItem,
								mediaItem.name
							);
						} else if (typeof mediaItem === "string") {
							formData.append(`variants[${variantIndex}].media`, mediaItem);
						}
					});
				}
			});
		}

		return apiService.put(`/products/${productId}/update`, formData);
	},

	updateProductStatus: (
		productId: string,
		isPublished: boolean
	): Promise<AxiosResponse<ApiResponse<any>>> => {
		// const formData = new FormData();
		// formData.append("status", isPublished ? "PUBLISHED" : "UNPUBLISHED");
		// formData.append("isDraft", "false");
		// return apiService.put(`/products/${productId}/update`, formData);
		return apiService.patch(
			`/products/${productId}/update-status/${
				isPublished ? "PUBLISHED" : "UNPUBLISHED"
			}`
		);
	},

	getMerchantProducts: ({
		page = 0,
		size = 10,
		...otherParams
	}: ProductFilter = {}): Promise<
		AxiosResponse<ApiResponse<ProductsResponse>>
	> => {
		// Directly append the query parameters to the URL to avoid nesting
		const url = `/products/of-merchant?page=${page}&size=${size}`;

		// Add any additional parameters if needed
		const otherParamsEntries = Object.entries(otherParams).filter(
			([_, value]) => value !== undefined
		);
		const additionalParams = otherParamsEntries
			.map(([key, value]) => `&${key}=${encodeURIComponent(String(value))}`)
			.join("");

		return apiService.get(url + additionalParams);
	},

	getProductToUpdate: (
		productId: string
	): Promise<AxiosResponse<ApiResponse<Product>>> =>
		apiService.get(`/products/${productId}/to-update`),

	addVariantMedia: (
		variantId: string,
		file: File
	): Promise<AxiosResponse<ApiResponse<any>>> => {
		const formData = new FormData();
		formData.append("media", file);
		return apiService.patch(
			`/products/${variantId}/add-variant-media`,
			formData
		);
	},
	
	deleteVariant: (
		productId: string,
		variantId: string
	): Promise<AxiosResponse<ApiResponse<any>>> => {
		return apiService.delete(
			`/products/${productId}/${variantId}`
		);
	},

	updateSizeChartMedia: (
		productId: string,
		file: File
	): Promise<AxiosResponse<ApiResponse<any>>> => {
		const formData = new FormData();
		formData.append("media", file);
		return apiService.patch(
			`/products/${productId}/update-size-chart-media`,
			formData
		);
	},

	getProductReviewRatings: (
		productId: string
	): Promise<AxiosResponse<ApiResponse<any>>> =>
		apiService.get(`/products/${productId}/review-ratings`),

	getCategoryByMastercategoryId: (
		categoryId: string
	): Promise<AxiosResponse<ApiResponse<any>>> =>
		apiService.get(`/products/categories/by-master-category/${categoryId}`),

	getMasterCategorybyCategory: (categoryId: string): Promise<any> =>
		apiService.get(`/products/master-categories/by-category/${categoryId}`),

	uploadBulkProductsExcel: (
		file: File
	): Promise<AxiosResponse<ApiResponse<any>>> => {
		const formData = new FormData();
		formData.append("excelFile", file);

		return apiService.post("/products/upload-bulk-products-excel", formData);
	},

	getDownloadTemplateExcel: (categoryId: string): Promise<any> => {
		return apiService.get(`/products/categories/${categoryId}/template-excel`, {
			responseType: "blob",
		});
	},

	getMerchantInventorySummary: (): Promise<
		AxiosResponse<
			ApiResponse<{
				totalSkus: number;
				outOfStockCount: number;
				pendingApprovalCount: number;
			}>
		>
	> => apiService.get("/products/merchant/inventory-summary"),
};

export default ProductController;
